import { addMinutes, format, parse } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import type { ScheduledMeeting } from '../../../contexts';
import { deriveMeetingType } from '../../../utils/meetingType';
import { DEFAULT_MEETING_TYPE } from '../helpers/constants';
import { getInitialFormData } from '../helpers/form';
import type { MeetingFormData } from '../types';

const useScheduleMeetingForm = (
  meeting?: ScheduledMeeting,
  defaultRoom?: string,
  defaultPasscode?: string,
  suggestedStartDate?: Date,
) => {
  const [formData, setFormData] = useState<MeetingFormData>(() => ({
    ...getInitialFormData(suggestedStartDate),
    room: meeting?.room ?? defaultRoom ?? '',
    password: meeting?.password ?? defaultPasscode ?? undefined,
  }));

  useEffect(() => {
    // This is a new meeting created on open, so we only update the room and title.
    // The date and time are preserved from the initial state.
    if (meeting) {
      // A saved personal meeting carries its own access policy, so show that
      // rather than silently rewriting it on submit. A freshly allocated room has
      // nothing to derive from and resets to the default, so switching away from a
      // personal meeting cannot leave that room's policy on the new one.
      const shouldDeriveMeetingTypeFromMeeting =
        Boolean(meeting.description) || (Boolean(defaultRoom) && meeting.room === defaultRoom);
      setFormData(
        (prev) =>
          ({
            ...prev,
            room: meeting.room,
            title: meeting.description ?? '',
            password: meeting.password ?? undefined,
            meetingType: shouldDeriveMeetingTypeFromMeeting
              ? deriveMeetingType(meeting)
              : DEFAULT_MEETING_TYPE,
          }) as MeetingFormData,
      );
    } else if (defaultRoom) {
      setFormData((prev) => ({
        ...prev,
        room: defaultRoom,
        password: defaultPasscode ?? prev.password,
      }));
    }
  }, [meeting, defaultRoom, defaultPasscode]);

  useEffect(() => {
    if (!suggestedStartDate) {
      return;
    }

    setFormData((prev) => {
      const endDate = addMinutes(suggestedStartDate, prev.duration);
      return {
        ...prev,
        fromDate: format(suggestedStartDate, 'yyyy-MM-dd'),
        fromTime: format(suggestedStartDate, 'HH:mm'),
        toDate: format(endDate, 'yyyy-MM-dd'),
        toTime: format(endDate, 'HH:mm'),
      };
    });
  }, [suggestedStartDate]);

  const handleInputChange = useCallback(
    <K extends keyof MeetingFormData>(field: K, value: MeetingFormData[K]) => {
      setFormData((prev) => {
        const newFormData = { ...prev, [field]: value };

        if (['fromDate', 'fromTime', 'duration'].includes(field)) {
          try {
            const fromDateStr = newFormData.fromDate;
            const fromTimeStr = newFormData.fromTime;
            const duration = newFormData.duration;

            if (fromDateStr && fromTimeStr && duration > 0) {
              const startDate = parse(
                `${fromDateStr} ${fromTimeStr}`,
                'yyyy-MM-dd HH:mm',
                new Date(),
              );
              if (!Number.isNaN(startDate.getTime())) {
                const endDate = addMinutes(startDate, duration);
                newFormData.toDate = format(endDate, 'yyyy-MM-dd');
                newFormData.toTime = format(endDate, 'HH:mm');
              }
            }
          } catch (error) {
            console.error('Error updating date/time:', error);
            return prev;
          }
        }

        return newFormData;
      });
    },
    [],
  );

  return {
    formData,
    handleInputChange,
  };
};

export default useScheduleMeetingForm;
